import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import { DeprecatedRuntimeBreakpoints } from '@duda-co/runtime-breakpoints';
import { logger } from '@shared-services/log-service';
import {
    DeprecatedResponsiveStyles,
    DeprecatedWidgetStyles,
} from 'client/widget-components/responsive-styles/deprecated/styled-components-utils';
import { CSSProperties } from 'react';
import { NewBPStyles } from './types';

const bpMigrationKeyMap = {
    [DeprecatedRuntimeBreakpoints.DEFAULT]: RuntimeMediaQuery.COMMON,
    [DeprecatedRuntimeBreakpoints.DESKTOP]: RuntimeMediaQuery.DESKTOP,
    [DeprecatedRuntimeBreakpoints.MOBILE_PORTRAIT]: RuntimeMediaQuery.MOBILE,
    [DeprecatedRuntimeBreakpoints.TABLET]: RuntimeMediaQuery.TABLET,
    [DeprecatedRuntimeBreakpoints.LARGE_DESKTOP]: RuntimeMediaQuery.DESKTOP,
};

type MigratedRuleset = Partial<Record<RuntimeMediaQuery, CSSProperties>>;

export function migrateOldBPStyles(
    deprecatedStyles: DeprecatedWidgetStyles,
    migrationStyleKeys: { [key: string]: string } = {}
): NewBPStyles {
    try {
        const styles = deprecatedStyles.rules || {};
        return Object.entries(styles).reduce(
            (
                styleResult: NewBPStyles,
                [stylePropKey, stylePropValue]: [
                    string,
                    DeprecatedResponsiveStyles
                ]
            ) => {
                const styleKey =
                    migrationStyleKeys[stylePropKey] || stylePropKey;
                const migrated = (styleResult[styleKey] ||
                    {}) as MigratedRuleset;

                Object.entries(bpMigrationKeyMap).forEach(
                    ([oldKey, newKey]) => {
                        const propValue =
                            stylePropValue[
                                oldKey as DeprecatedRuntimeBreakpoints
                            ] ||
                            stylePropValue[
                                newKey as unknown as DeprecatedRuntimeBreakpoints
                            ];
                        if (propValue) {
                            // `desktop` (768+) and `large_desktop` (1440+) both fold onto the
                            // single current `desktop`, so merge their declarations instead of
                            // letting whichever is handled last replace the whole ruleset.
                            migrated[newKey] = {
                                ...(migrated[newKey] || {}),
                                ...propValue,
                            };
                        }
                    }
                );

                /**
                 * The deprecated `desktop` meant 768px and up, which spans both the current
                 * `tablet` (768–1024) and the current `desktop` (1025+), so its declarations have
                 * to seed `tablet` as well or the 768–1024 band stops rendering as it did before
                 * the migration. The seeding is unconditional and resolved per property: a
                 * deprecated `tablet` was the narrower rule, so every property it declares still
                 * wins, while the properties it leaves out keep coming from `desktop` — which is
                 * exactly what the two deprecated breakpoints did side by side.
                 * `large_desktop` (1440+) never applied at tablet widths, so it is deliberately
                 * not a source here.
                 */
                const deprecatedDesktop =
                    stylePropValue[DeprecatedRuntimeBreakpoints.DESKTOP];
                if (deprecatedDesktop) {
                    migrated[RuntimeMediaQuery.TABLET] = {
                        ...deprecatedDesktop,
                        ...(migrated[RuntimeMediaQuery.TABLET] || {}),
                    };
                }

                styleResult[styleKey] = migrated;
                return styleResult;
            },
            {} as NewBPStyles
        );
    } catch (e) {
        logger.error({
            message: `Failed to migrate old BP styles structure`,
            errorMessage: e?.toString(),
            tags: [
                'widget migration',
                'style migration',
                'old breakpoints to new',
            ],
        });
        throw e;
    }
}
